/**
 * Shared renderer for a placed image element. Applies the two per-element
 * looks that must match the PDF exactly:
 *   • crop — the stored crop region (percent of the ORIGINAL image)
 *     cover-fits the element box, mirroring the exporter's cropCoverRect;
 *     the original image is never modified,
 *   • edge — rounded / feather / vignette / torn / brush / scallop
 *     softening, using the same SPREAD_IMAGE_EDGES parameters (and, for
 *     the SVG-masked kinds, the same edgeMaskSvg shapes) the exporter
 *     bakes into the PDF,
 *   • shape — a cutout silhouette (shapeEdgeMaskSvg) the author
 *     positioned; an edge treatment stacked on it softens the resulting
 *     cut (the visible silhouette) via the same combined mask.
 *
 * Used by the editor canvas, the read-only StaticSpread, and the mini
 * navigator (minis skip edge/shape masks — their cqw units would resolve
 * to the wrong container — but render the crop, which dominates
 * composition).
 */
import { useState, type CSSProperties } from 'react';
import type { SpreadImageElement } from '@historyjki/shared';
import { SPREAD_DESIGN_W } from '@historyjki/shared';
import { cropCoverRect, SPREAD_IMAGE_EDGES } from '../../utils/spreadGeometry';
import { imageEdgeCss, imageShapeCss } from '../../utils/imageEdgeCss';
import { r2Url } from '../../utils/r2Url';

/** 1 design unit in canvas-width-relative cqw (same rule as font sizes). */
const DESIGN_UNIT_CQW = 100 / SPREAD_DESIGN_W;

interface SpreadImageProps {
  el: SpreadImageElement;
  /** Width/height ratio of the ELEMENT box on the rendered canvas. */
  boxAspect: number;
  /** Skip edge masks (mini navigator). */
  plainEdges?: boolean;
}

export function SpreadImage({ el, boxAspect, plainEdges }: SpreadImageProps) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  const shape = plainEdges ? null : (el.shape ?? null);
  const edge = plainEdges ? 'sharp' : (el.edge ?? 'sharp');
  const wrapStyle: CSSProperties = shape
    ? imageShapeCss(shape, boxAspect, edge)
    : imageEdgeCss(
        edge,
        boxAspect,
        `${SPREAD_IMAGE_EDGES.rounded.radius * DESIGN_UNIT_CQW}cqw`,
      );

  // Until the natural size is known a cropped image renders cover-fit
  // uncropped for a frame; images are cached so this is rarely visible.
  let imgStyle: CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' };
  if (el.crop && dims && boxAspect > 0) {
    // Box-relative units: width 1 × height 1/boxAspect, so the resulting
    // rect converts straight to CSS percentages of the wrapper.
    const bh = 1 / boxAspect;
    const r = cropCoverRect({ x: 0, y: 0, width: 1, height: bh }, dims.w, dims.h, el.crop);
    imgStyle = {
      position: 'absolute',
      left: `${r.x * 100}%`,
      top: `${(r.y / bh) * 100}%`,
      width: `${r.width * 100}%`,
      height: `${(r.height / bh) * 100}%`,
      maxWidth: 'none',
    };
  }

  return (
    <div className="cel__imgwrap" style={wrapStyle}>
      <img
        src={r2Url(el.r2_key)}
        alt={el.caption ?? ''}
        draggable={false}
        loading="lazy"
        style={imgStyle}
        onLoad={(e) => {
          const t = e.currentTarget;
          if (t.naturalWidth > 0 && t.naturalHeight > 0) {
            setDims({ w: t.naturalWidth, h: t.naturalHeight });
          }
        }}
      />
    </div>
  );
}
